/**
 * Comandos do chat da mesa sobre o gerente de comandos (docs/spec/chat-commands.md).
 * Os gatilhos dos dois idiomas ficam sempre registrados; o canônico (o que aparece
 * na ajuda) segue o idioma escolhido.
 */
import { CommandManager, error, notExecuted, type ChatPersona, type CommandEntry } from '../shared.js';
import { rollProblem } from '../dice/rollCheck.js';
import { convertLength, type LengthUnit } from '../scene/units.js';

export type ChatCommandEnv = {
  t: (key: string, params?: Record<string, string | number>) => string;
  sendChat: (text: string, kind: 'chat' | 'action' | 'laugh', persona?: ChatPersona) => void;
  rollDice: (expression: string) => void;
  leaveRoom: (farewell?: string) => void;
  setNick: (nick: string | null) => void;
  persona: () => ChatPersona | undefined;
  /** cls / fls / prink / reload: só mexem no log de quem digitou */
  hideBefore: (at: number) => void;
  hideSystem: (hide: boolean) => void;
  /** liga e desliga o /audit (docs/spec/chat-delete.md); ausente para quem não é mestre e nas conversas privadas */
  toggleAudit?: (() => void) | undefined;
  /** escreve uma linha local (só para quem digitou) */
  say: (text: string) => void;
  /** abre o gerenciador de macros */
  openMacros: () => void;
};

type Lang = 'pt-BR' | 'en';

/** Gatilho canônico primeiro, conforme o idioma. */
const TRIGGERS: Record<string, Record<Lang, string[]>> = {
  help: { 'pt-BR': ['ajuda', 'help'], en: ['help', 'ajuda'] },
  roll: { 'pt-BR': ['rolar', 'r', 'roll'], en: ['roll', 'r', 'rolar'] },
  me: { 'pt-BR': ['me'], en: ['me'] },
  laugh: { 'pt-BR': ['rir', 'lol'], en: ['lol', 'rir'] },
  cls: { 'pt-BR': ['cls'], en: ['cls'] },
  fls: { 'pt-BR': ['fls'], en: ['fls'] },
  prink: { 'pt-BR': ['prink'], en: ['prink'] },
  reload: { 'pt-BR': ['reload'], en: ['reload'] },
  audit: { 'pt-BR': ['audit'], en: ['audit'] },
  part: { 'pt-BR': ['partir', 'leave'], en: ['leave', 'partir'] },
  nick: { 'pt-BR': ['nick'], en: ['nick'] },
  macros: { 'pt-BR': ['macros'], en: ['macros'] },
  fm: { 'pt-BR': ['fm'], en: ['fm'] },
  mf: { 'pt-BR': ['mf'], en: ['mf'] },
};

/** O número depois de /fm e /mf: `40`, `2,5` ou `2.5`. */
const LENGTH_ARG = /^\d+(?:[.,]\d+)?$/;

export function createChatCommands(language: Lang, t: ChatCommandEnv['t']): CommandManager<ChatCommandEnv> {
  const tr = (id: string) => TRIGGERS[id]![language];
  const manual = (id: string) => () => ({ title: t(`cmd.${id}.title`), lines: t(`cmd.${id}.desc`).split('\n') });
  /** "12 m", "1 pé", "196,85 pés": duas casas no máximo, e o singular só quando o número mostrado é 1. */
  const length = (value: number, unit: LengthUnit) => {
    const name = unit === 'm' ? t('scene.unit.m') : t(Math.round(value * 100) === 100 ? 'scene.unit.foot' : 'scene.unit.feet');
    return `${value.toLocaleString(language, { maximumFractionDigits: 2 })} ${name}`;
  };
  /** /fm (pés → metros) e /mf (metros → pés): a conta dos livros e a exata, só para quem digitou. */
  const lengthCommand = (id: 'fm' | 'mf', from: LengthUnit): CommandEntry<ChatCommandEnv> => ({
    triggers: tr(id),
    manual: manual(id),
    execute: (req, env) => {
      const arg = req.args.trim();
      if (!LENGTH_ARG.test(arg)) return error(t(`cmd.${id}.empty`));
      const value = Number(arg.replace(',', '.'));
      const { to, book, exact } = convertLength(value, from);
      const params = { from: length(value, from), to: length(book, to), exact: length(exact, to) };
      env.say(t(params.to === params.exact ? 'cmd.length.same' : 'cmd.length.result', params));
    },
  });
  const manager = new CommandManager<ChatCommandEnv>({
    prefix: '/',
    helpTrigger: tr('help')[0]!,
    messages: {
      unknownCommand: (commandId, help) => t('cmd.unknown', { cmd: commandId, help: help ?? '' }),
      errorWithoutMessage: (commandId) => t('cmd.failed', { cmd: commandId }),
    },
  });

  manager
    .register({
      triggers: tr('help'),
      manual: manual('help'),
      execute: (_req, env) => {
        const lines = manager.manuals().map(({ manual: m }) => `**${m.title}** — ${m.lines.join(' ')}`);
        env.say([`**${t('cmd.help.heading')}**`, ...lines, '', t('cmd.help.formatting')].join('\n'));
      },
    })
    .register({
      triggers: tr('roll'),
      manual: manual('roll'),
      execute: (req, env) => {
        if (!req.args) return error(t('cmd.roll.empty'));
        const problem = rollProblem(req.args, t);
        if (problem) return error(`⚠ ${problem}`);
        env.rollDice(req.args);
      },
    })
    .register({
      triggers: tr('me'),
      manual: manual('me'),
      execute: (req, env) => (req.args ? env.sendChat(req.args, 'action', env.persona()) : notExecuted(t('cmd.me.empty'))),
    })
    .register({ triggers: tr('laugh'), manual: manual('laugh'), execute: (req, env) => env.sendChat(req.args || t('cmd.laugh.default'), 'laugh') })
    .register({ triggers: tr('cls'), manual: manual('cls'), execute: (_req, env) => env.hideBefore(Date.now()) })
    .register({ triggers: tr('fls'), manual: manual('fls'), execute: (_req, env) => env.hideBefore(0) })
    .register({ triggers: tr('prink'), manual: manual('prink'), execute: (_req, env) => env.hideSystem(true) })
    .register({
      triggers: tr('reload'),
      manual: manual('reload'),
      execute: (_req, env) => {
        env.hideSystem(false);
        env.hideBefore(0);
      },
    })
    .register({ triggers: tr('audit'), manual: manual('audit'), execute: (_req, env) => (env.toggleAudit ? env.toggleAudit() : error(t('cmd.audit.gmOnly'))) })
    .register({ triggers: tr('part'), manual: manual('part'), execute: (req, env) => env.leaveRoom(req.args || undefined) })
    .register({ triggers: tr('nick'), manual: manual('nick'), execute: (req, env) => env.setNick(req.args || null) })
    .register({ triggers: tr('macros'), manual: manual('macros'), execute: (_req, env) => env.openMacros() })
    .register(lengthCommand('fm', 'ft'))
    .register(lengthCommand('mf', 'm'));

  return manager;
}
